import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * Restores the Supabase session on mount and keeps the auth store in sync.
 *
 * Supabase SDK persists the session in its own localStorage key — we just
 * need to read it back on startup and subscribe to future changes (token
 * refresh, sign-out, etc.).
 *
 * Shows a full-screen loader until the initial session check completes so
 * RequireAuth never fires before we know whether the user is authenticated.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const setSession = useAuthStore((s) => s.setSession)
  const setInitialized = useAuthStore((s) => s.setInitialized)
  const initialized = useAuthStore((s) => s.initialized)

  useEffect(() => {
    // Read the session Supabase already persisted in localStorage
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setInitialized(true)
    })

    // Stay in sync with token refresh, sign-out, etc.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Memuat...</p>
      </div>
    )
  }

  return <>{children}</>
}
